// File: frontend/src/components/ems/EventDetailsModal.js

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  X, 
  Activity, 
  Calendar, 
  User, 
  Database, 
  Code, 
  Link as LinkIcon,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import LoadingSpinner from '../utility/LoadingSpinner';
import styles from './EventDetailsModal.module.css';

const EventDetailsModal = ({ 
  isOpen, 
  onClose, 
  eventDetails, 
  isLoading,
  onViewUser 
}) => {
  // Scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo({ top: scrollY, behavior: 'instant' });
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    }).format(date);
  };

  const formatEventType = (eventType) => {
    if (!eventType) return '-';
    return eventType.replace(/_/g, ' ');
  };

  const getEventTypeBadgeClass = (eventType) => {
    if (!eventType) return styles.badgeDefault;
    if (eventType.includes('SIGN_IN') || eventType.includes('SIGN_UP')) {
      return styles.badgeSuccess;
    }
    if (eventType.includes('ERROR') || eventType.includes('FAIL')) {
      return styles.badgeError;
    }
    if (eventType.includes('EMAIL') || eventType.includes('NOTIFICATION')) {
      return styles.badgeInfo;
    }
    if (eventType.includes('UPDATE') || eventType.includes('CHANGE')) {
      return styles.badgeWarning;
    }
    return styles.badgeDefault;
  };

  const modalContent = (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerLeft}>
            <Activity size={24} />
            <h2 className={styles.modalTitle}>Event Details</h2>
          </div>
          <button
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={styles.modalBody} data-lenis-prevent>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <LoadingSpinner size="large" text="Loading event details..." />
            </div>
          ) : eventDetails ? (
            <>
              {/* Basic Information */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Database size={18} />
                  Basic Information
                </h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Event ID</span>
                    <span className={styles.detailValue}>{eventDetails.event_id}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Event Type</span>
                    <span className={`${styles.badge} ${getEventTypeBadgeClass(eventDetails.event_type)}`}>
                      {formatEventType(eventDetails.event_type)}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Timestamp</span>
                    <span className={styles.detailValue}>{formatDate(eventDetails.event_timestamp)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Aggregate ID</span>
                    <span className={styles.detailValue}>{eventDetails.aggregate_id}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Created By</span>
                    <span className={styles.detailValue}>{eventDetails.created_by}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Correlation ID</span>
                    <span className={styles.detailValue}>{eventDetails.correlation_id || '-'}</span>
                  </div>
                </div>
              </section>

              {/* User Context */}
              {eventDetails.user_context && eventDetails.user_context.found && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <User size={18} />
                    User Context
                  </h3>
                  <div className={styles.detailGrid}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>User ID</span>
                      <button
                        onClick={() => onViewUser(eventDetails.user_context.user_id)}
                        className={styles.userLinkButton}
                      >
                        {eventDetails.user_context.user_id}
                        <ExternalLink size={14} />
                      </button>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Name</span>
                      <span className={styles.detailValue}>
                        {eventDetails.user_context.first_name} {eventDetails.user_context.last_name}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Email</span>
                      <span className={styles.detailValue}>{eventDetails.user_context.email}</span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Role</span>
                      <span className={`${styles.badge} ${styles.badgeInfo}`}>
                        {eventDetails.user_context.user_role}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Status</span>
                      <span className={`${styles.badge} ${
                        eventDetails.user_context.user_status === 'active' 
                          ? styles.badgeSuccess 
                          : styles.badgeWarning
                      }`}>
                        {eventDetails.user_context.user_status}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Anonymous</span>
                      <span className={`${styles.badge} ${
                        eventDetails.user_context.is_anonymous 
                          ? styles.badgeWarning 
                          : styles.badgeSuccess
                      }`}>
                        {eventDetails.user_context.is_anonymous ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </section>
              )}

              {/* Event Data */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Code size={18} />
                  Event Data
                </h3>
                <pre className={styles.jsonViewer}>
                  {JSON.stringify(eventDetails.event_data, null, 2)}
                </pre>
              </section>

              {/* Metadata */}
              {eventDetails.metadata && Object.keys(eventDetails.metadata).length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <Database size={18} />
                    Metadata
                  </h3>
                  <pre className={styles.jsonViewer}>
                    {JSON.stringify(eventDetails.metadata, null, 2)}
                  </pre>
                </section>
              )}

              {/* Related Events */}
              {eventDetails.related_events && eventDetails.related_events.length > 0 && (
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <LinkIcon size={18} />
                    Related Events ({eventDetails.related_events_count || eventDetails.related_events.length})
                  </h3>
                  <div className={styles.relatedEventsList}>
                    {eventDetails.related_events.map((relEvent, index) => (
                      <div key={relEvent.event_id || index} className={styles.relatedEventCard}>
                        <div className={styles.relatedEventHeader}>
                          <span className={`${styles.badge} ${styles.badgeSmall} ${getEventTypeBadgeClass(relEvent.event_type)}`}>
                            {formatEventType(relEvent.event_type)}
                          </span>
                          <span className={styles.relatedEventTime}>
                            {formatDate(relEvent.event_timestamp)}
                          </span>
                        </div>
                        {relEvent.event_id && (
                          <div className={styles.relatedEventId}>
                            ID: {relEvent.event_id}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Audit Trail */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <Calendar size={18} />
                  Audit Trail
                </h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Source System</span>
                    <span className={styles.detailValue}>{eventDetails.source_system || '-'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Source IP</span>
                    <span className={styles.detailValue}>{eventDetails.source_ip || '-'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>User Agent</span>
                    <span className={styles.detailValue}>
                      {eventDetails.user_agent || eventDetails.metadata?.user_agent || '-'}
                    </span>
                  </div>
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Session ID</span>
                    <span className={styles.detailValue}>
                      {eventDetails.session_id || eventDetails.metadata?.session_id || '-'}
                    </span>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <div className={styles.errorContainer}>
              <AlertCircle size={48} />
              <p>Failed to load event details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EventDetailsModal;